import React, { useEffect, useState } from "react";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import SettingsModal from "../../../types/settingsModal";
import SearchWrapper from "./SearchContainer";
import { useSelector } from "../../../store";
import { debounce } from "lodash";
import ColumnDef from "../../../types/columnDef";
import { Base } from "../../base";
import CalendarHighlight from "../../../types/calendarHighlight";
import AddEditCalendarHighlightForm from "../../forms/AddEditCalendarHighlightForm";
import { Box, Grid } from "@mui/material";

interface Props {
  apiHighlightSearch: (search: SearchParams) => Promise<any>;
  apiGetHighlight: (id: number) => void;
  resetHighlight: () => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setHighlightFilter: (params: SearchParams) => void;
  deleteModal: SettingsModal;
}

const CalendarHighlightView: React.FC<Props> = (props) => {
  const {
    apiHighlightSearch,
    apiGetHighlight,
    resetHighlight,
    phone,
    setDeleteModal,
    setHighlightFilter,
    deleteModal,
  } = props;
  const { highlightFilter, highlightSearchResults, status } = useSelector(
    (store) => store.highlight
  );
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [addEditModal, setAddEditModal] = useState<boolean>(false);
  const [highlightId, setHighlightId] = useState(0);

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setHighlightFilter(defaultParams);
    };
  }, [phone]);

  useEffect(() => {
    if (
      !deleteModal.visible &&
      deleteModal.modal === undefined &&
      highlightId > 0
    ) {
      setHighlightId(0);
    }
  }, [deleteModal.modal, deleteModal.visible, highlightId]);

  const updateSearch = debounce((query) => {
    setHighlightFilter({
      ...highlightFilter,
      query,
      pageNumber: phone ? 2 : 1,
      pageSize: 10,
    });
    apiHighlightSearch({
      ...highlightFilter,
      query,
      pageNumber: 1,
      pageSize: 10,
    }).then((result: any) => {
      setMobileRows(result);
    });
  }, 500);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Source",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "color",
      headerName: "Color",
      headerAlign: "center",
      align: "center",
      flex: 0.25,
      headerClassName: "header",
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((ch: CalendarHighlight) => {
      let rowObject = {
        id: ch.id,
        name: ch.name,
        color: (
          <Grid container justifyContent={"center"} alignItems={"center"}>
            <Box
              sx={{ height: "40px", width: "100%", backgroundColor: ch.color }}
            />
          </Grid>
        ),
        actions: (
          <Base.TableActions
            id={ch.id}
            handleDelete={() => {
              apiGetHighlight(ch.id);
              setDeleteModal({ visible: true, modal: "highlight" });
              setHighlightId(ch.id);
            }}
            handleEdit={() => {
              apiGetHighlight(ch.id);
              setAddEditModal(true);
              setHighlightId(ch.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiHighlightSearch({
      ...highlightFilter,
      pageNumber,
      pageSize,
    }).then(() => {
      setHighlightFilter({
        ...highlightFilter,
        pageNumber,
        pageSize,
      });
    });
  };

  const next = () => {
    apiHighlightSearch(highlightFilter).then((result: any) => {
      setHighlightFilter({
        ...highlightFilter,
        pageNumber: highlightFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };
  const close = () => {
    apiHighlightSearch(defaultParams);
    setHighlightFilter(defaultParams);
    setAddEditModal(false);
    resetHighlight();
    setHighlightId(0);
  };

  const handleSaveUpdate = () => {
    apiHighlightSearch(defaultParams).then((result: any) => {
      setMobileRows(result);
      setHighlightFilter({ ...defaultParams, pageNumber: 2 });
    });
  };

  return (
    <>
      <SearchWrapper
        updateSearch={updateSearch}
        searchValue={highlightFilter.query}
        modalOpen={setAddEditModal}
        reset={resetHighlight}
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(highlightSearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={highlightFilter.pageNumber}
          pageSize={highlightFilter.pageSize}
          totalRows={highlightSearchResults?.totalResults}
          loading={status === "loading"}
          highlightId={highlightId}
          changePage={changePage}
          next={next}
        />
      </SearchWrapper>
      <Base.Modal open={addEditModal} onClose={close}>
        <AddEditCalendarHighlightForm close={close} onSave={handleSaveUpdate} />
      </Base.Modal>
    </>
  );
};

export default CalendarHighlightView;
